import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Share2, Copy, Check, ImageDown, FileDown, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import ExportSheet from "./ExportSheet";
import { useI18n } from "../i18n";

export default function ShareExport({ shareUrl, data }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const sheetRef = useRef(null);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard copy failed:", e);
      toast.error(t("copyFail"));
    }
  };

  const capture = async () => {
    const node = sheetRef?.current;
    if (!node) return null;
    return await html2canvas(node, {
      backgroundColor: "#FDFBF7",
      scale: 2,
      useCORS: true,
      windowWidth: 816,
    });
  };

  const downloadImage = async () => {
    try {
      setBusy(true);
      const canvas = await capture();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = "my-cycle.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(t("imageDownloaded"));
    } catch (e) {
      console.error("Image export failed:", e);
      toast.error(t("exportFail"));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setBusy(true);
      const canvas = await capture();
      if (!canvas) return;
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const ratio = Math.min((pw - margin * 2) / canvas.width, (ph - margin * 2) / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, "PNG", (pw - w) / 2, margin, w, h);
      pdf.save("my-cycle.pdf");
      toast.success(t("pdfDownloaded"));
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error(t("exportFail"));
    } finally {
      setBusy(false);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Cycle IQ",
          text: t("shareText"),
          url: shareUrl,
        });
      } catch (e) {
        // AbortError = user dismissed the share sheet; anything else is worth logging
        if (e && e.name !== "AbortError") {
          console.error("Native share failed:", e);
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <>
    <Dialog>
      <DialogTrigger asChild>
        <Button
          data-testid="open-share-btn"
          variant="outline"
          className="h-10 w-10 p-0 rounded-full border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-sm max-h-[88dvh] overflow-y-auto rounded-3xl bg-bone border-border p-5 sm:p-6">
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-serif text-2xl text-left">
            {t("shareBtnTitle")}
          </DialogTitle>
          <DialogDescription className="text-left text-sm text-muted-foreground">
            {t("shareDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 min-w-0">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-white p-2.5 min-w-0">
            <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span
              data-testid="share-url-text"
              className="text-xs text-foreground/80 truncate flex-1 min-w-0"
            >
              {shareUrl}
            </span>
          </div>
          <Button
            data-testid="copy-link-btn"
            onClick={copyLink}
            className="w-full mt-3 h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-transform"
          >
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? t("copied") : t("copyLink")}
          </Button>
          <Button
            data-testid="native-share-btn"
            onClick={nativeShare}
            variant="outline"
            className="w-full mt-2 h-11 rounded-full border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
          >
            <Share2 className="h-4 w-4 mr-2" />
            {t("shareVia")}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3 pt-4 border-t border-border min-w-0">
          <Button
            data-testid="download-image-btn"
            onClick={downloadImage}
            disabled={busy}
            variant="outline"
            className="h-11 rounded-full border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
          >
            <ImageDown className="h-4 w-4 mr-2" />
            {t("image")}
          </Button>
          <Button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            disabled={busy}
            variant="outline"
            className="h-11 rounded-full border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {t("pdf")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", zIndex: -1 }} aria-hidden>
      <ExportSheet ref={sheetRef} data={data} />
    </div>
    </>
  );
}
